import {
  GetAccountDetailsQuery,
  GetUserInfoQuery,
  ProductName,
} from '@pypestream/api-services/urql';
import { OrganizationRoleNames } from '../hooks';
import { treeUtils } from './manager.xstate';
import { sortByKeyRecursively } from '@pypestream/design-system';
import { sortBy } from 'lodash';
import { AccountRole } from '../components/permissions-table/permissions-table.types';

export const ProductNames = {
  PLATFORM: 'PLATFORM',
  ORGANIZATION: 'ORGANIZATION',
  AGENT_ASSIST: 'AGENT_ASSIST',
  ANALYTICS: 'ANALYTICS',
};

export const AnalyticsRoleNames = {
  ANALYTICS_ADMIN: 'Analytics Admin',
  ANALYTICS_VIEWER: 'Analytics Viewer',
  ANALYTICS_MEMBER: 'Analytics Member',
  PYPESTREAM_DATA_TEAM: 'Pypestream Data Team',
};

const PYPESTREAM_EMPLOYEE_EMAIL_DOMAIN = '@pypestream.com';

// Keep in sync with the backend's isPypestreamEmployeeEmail — the
// "Pypestream Data Team" role may only be held by Pypestream employees.
export const isPypestreamEmployeeEmail = (email?: string | null): boolean =>
  !!email?.toLowerCase().endsWith(PYPESTREAM_EMPLOYEE_EMAIL_DOMAIN);

export const AgentAssistRoleNames = {
  AGENT_ASSIST_ADMIN: 'Agent Manager',
  AGENT_ASSIST_AGENT: 'Agent',
};

export const getRoleLevelForAccountRoles = (
  accountRoleName: string
): { roleLevel: number } => {
  switch (accountRoleName) {
    case OrganizationRoleNames.SUPER_ADMIN:
      return { roleLevel: 1 };
    case OrganizationRoleNames.ADMIN:
      return { roleLevel: 2 };
    case OrganizationRoleNames.MANAGER:
      return { roleLevel: 3 };
    default:
      return { roleLevel: 0 };
  }
};

export const productsSortFunction = (
  a: { product?: { name?: string } },
  b: { product?: { name?: string } }
) => {
  const order = [
    ProductName.Organization,
    ProductName.AgentAssist,
    ProductName.Analytics,
    ProductName.ProStudio,
    ProductName.MaestroStudio,
  ];

  const indexA = order.findIndex((o) => o === a.product?.name);

  const indexB = order.findIndex((o) => o === b.product?.name);

  return indexA - indexB;
};

export const getLoggedInUserHighestRole = (
  assignedRoles: NonNullable<
    NonNullable<GetUserInfoQuery['admin_']>['currentUser']
  >['assignedRoles'] = [],
  assignedTeams: NonNullable<
    NonNullable<GetUserInfoQuery['admin_']>['currentUser']
  >['assignedTeams'] = []
): NonNullable<typeof assignedRoles>[number] | undefined => {
  const allUserRoles: typeof assignedRoles = [];

  assignedRoles.forEach((assignedRole) => {
    const foundRole = allUserRoles.find(
      (userRole) => userRole.id === assignedRole.id
    );
    if (!foundRole) {
      allUserRoles.push(assignedRole);
    }
  });

  assignedTeams.forEach((assignedTeam) => {
    assignedTeam.assignedRoles?.forEach((assignedRole) => {
      const foundRole = allUserRoles.find(
        (userRole) => userRole.id === assignedRole.id
      );
      if (!foundRole) {
        allUserRoles.push(assignedRole);
      }
    });
  });

  return allUserRoles
    ?.map((loggedInUserAssignedRole) => ({
      ...getRoleLevelForAccountRoles(loggedInUserAssignedRole.name || ''),
      ...loggedInUserAssignedRole,
    }))
    .sort((a, b) => a.roleLevel - b.roleLevel)
    .shift();
};

export const handleOrgsDataProcessing = (
  orgDetails: GetAccountDetailsQuery
): NonNullable<
  NonNullable<
    NonNullable<GetAccountDetailsQuery['admin_']>['accountById']
  >['allChildAccounts']
> => {
  let allOrgs = [];

  if (orgDetails?.admin_?.accountById) {
    const {
      allChildAccounts,
      ...rest
    }: NonNullable<GetAccountDetailsQuery['admin_']>['accountById'] =
      orgDetails.admin_.accountById;
    // Recalculating org tree with respect to selected user's home-org.
    allOrgs = treeUtils.tree2List(
      treeUtils.list2Tree(
        [{ ...rest }, ...sortByKeyRecursively(allChildAccounts || [], 'name')],
        rest.parentId
      )
    );
  }
  return allOrgs;
};

export const processAccountRoles = (
  accountRoles: AccountRole[],
  loggedInUserHighestRole: NonNullable<
    NonNullable<
      NonNullable<GetUserInfoQuery['admin_']>['currentUser']
    >['assignedRoles']
  >[number],
  orgRoles: NonNullable<
    NonNullable<GetAccountDetailsQuery['admin_']>['accountById']
  >['roles']
): {
  accountRoles: AccountRole[];
  availableAccountRoles: AccountRole[];
  roleIdsToRemove: string[];
} => {
  const roleIdsToRemove: string[] = [];
  let availableAccountRoles: AccountRole[] = [];

  const sortedAssignedAccountRoles = accountRoles
    ?.map((accountRole) => ({
      ...getRoleLevelForAccountRoles(accountRole.role.name || ''),
      ...accountRole,
    }))
    .sort((a, b) => a.roleLevel - b.roleLevel);

  const highestAssignedRole = sortedAssignedAccountRoles.shift();

  sortedAssignedAccountRoles.forEach((assignedRole) => {
    roleIdsToRemove.push(assignedRole.role.id);
  });

  if (loggedInUserHighestRole) {
    if (loggedInUserHighestRole.name === OrganizationRoleNames.SUPER_ADMIN) {
      orgRoles?.rows
        ?.filter(
          (orgRole) => orgRole.product?.name === ProductNames.ORGANIZATION
        )
        .forEach((orgRole) => {
          if (orgRole.id !== highestAssignedRole?.role.id) {
            availableAccountRoles.push({
              role: {
                id: orgRole.id || '',
                name: orgRole.name || '',
                productId: orgRole.productId || '',
                product: {
                  id: orgRole.productId || '',
                  name: orgRole.product?.name || '',
                  displayName: orgRole.product?.displayName || '',
                },
                accountId: orgRole.accountId || '',
              },
            });
          }
        });
    } else if (loggedInUserHighestRole.name === OrganizationRoleNames.ADMIN) {
      orgRoles?.rows
        ?.filter(
          (orgRole) => orgRole.product?.name === ProductNames.ORGANIZATION
        )
        .forEach((orgRole) => {
          if (
            orgRole.id !== highestAssignedRole?.role.id &&
            orgRole.name !== OrganizationRoleNames.SUPER_ADMIN
          ) {
            availableAccountRoles.push({
              role: {
                id: orgRole.id || '',
                name: orgRole.name || '',
                productId: orgRole.productId || '',
                product: {
                  id: orgRole.productId || '',
                  name: orgRole.product?.name || '',
                  displayName: orgRole.product?.displayName || '',
                },
                accountId: orgRole.accountId || '',
              },
            });
          }
        });
    } else if (loggedInUserHighestRole.name === OrganizationRoleNames.MANAGER) {
      orgRoles?.rows
        ?.filter(
          (orgRole) => orgRole.product?.name === ProductNames.ORGANIZATION
        )
        .forEach((orgRole) => {
          if (
            orgRole.id !== highestAssignedRole?.role.id &&
            orgRole.name !== OrganizationRoleNames.SUPER_ADMIN &&
            orgRole.name !== OrganizationRoleNames.ADMIN
          ) {
            availableAccountRoles.push({
              role: {
                id: orgRole.id || '',
                name: orgRole.name || '',
                productId: orgRole.productId || '',
                product: {
                  id: orgRole.productId || '',
                  name: orgRole.product?.name || '',
                  displayName: orgRole.product?.displayName || '',
                },
                accountId: orgRole.accountId || '',
              },
            });
          }
        });
    }
  } else {
    availableAccountRoles = [];
  }

  return {
    accountRoles: (highestAssignedRole && [highestAssignedRole]) || [],
    availableAccountRoles: sortBy(availableAccountRoles, 'role.name'),
    roleIdsToRemove,
  };
};
